import { number, object, string, mixed, TestContext } from 'yup';
import {
  MIN_WITHDRAW_PAYPAL_AMOUNTS,
  PAYPAL_WITHDRAWAL_PLATFORM_FEE_PERCENT,
  SystemCurrency,
} from '@okletsplay/core';
import 'modules/payments/validators';
import { ExchangeRateDto, UserBalanceDto } from 'app/interfaces/dto';
import { convertReverseCrypto, formatCrypto } from 'shared/utils';
import { UserQuotaStatusDto } from 'app/interfaces/dto/user-quota-status.dto';
import { AnyObject } from 'yup/lib/types';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { quotasValidator } from 'modules/payments/validators';
import { QuotaOperation } from '../enums';
import { PAYPAL_CONFIG } from 'config/paypal-config';

type Context = TestContext<AnyObject>;

const SOMETHING_WRONG_MESSAGE = 'Something went wrong, try later';

export const buildWithdrawRequestFormSchema = (
  getUserAccounts: () => (UserBalanceDto | undefined)[],
  getExchangeRates: () => (ExchangeRateDto | undefined)[],
  getQuotas: () => UserQuotaStatusDto | undefined
) => {
  const validateQuotas = (value: number, currency: SystemCurrency, context: Context) => {
    let amount: number;
    if (currency === SystemCurrency.USDT) {
      amount = value;
    } else if (currency === SystemCurrency.OKLP) {
      const exchangeRates = getExchangeRates();

      const rate = exchangeRates.find(
        (exchange) => exchange?.from === SystemCurrency.USDT && exchange?.to === SystemCurrency.OKLP
      )?.rate;

      if (!rate) {
        throw context.createError({ message: SOMETHING_WRONG_MESSAGE });
      }

      amount = convertReverseCrypto(value, rate);
    } else {
      throw context.createError({ message: SOMETHING_WRONG_MESSAGE });
    }

    const amountWithoutFee = amount - (amount * PAYPAL_WITHDRAWAL_PLATFORM_FEE_PERCENT) / 100;

    quotasValidator({
      getQuotas,
      context,
      mainCurrencyAmount: amountWithoutFee,
      quotaOperation: QuotaOperation.Withdrawal,
    });
  };

  const testAmount = (value: number | undefined, context: Context) => {
    if (!value) {
      throw context.createError({ message: 'Provide an amount to withdraw' });
    }

    const { currency } = context.parent;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const minAmount = MIN_WITHDRAW_PAYPAL_AMOUNTS[currency];
    if (value < minAmount) {
      throw context.createError({
        message: `Minimum amount is ${formatCrypto(minAmount, currency)}`,
      });
    }

    const userAccounts = getUserAccounts();
    const balance = userAccounts.find((account) => account?.currency === currency)?.balance ?? 0;

    if (value > balance) {
      throw context.createError({
        message: 'Amount must be less or equal available balance, use MAX option instead',
      });
    }

    if (MultiCurrencyConfig.isUserQuotaWithdrawalEnabled()) {
      validateQuotas(value, currency, context);
    }

    return true;
  };

  const countryNotInListErrorMessage = `International withdrawals coming soon. List of allowed countries: ${PAYPAL_CONFIG.withdrawAllowedCountryNames}.`;

  const countryValidation = PAYPAL_CONFIG.isInternationalWithdrawEnabled
    ? string().label('Country').optional()
    : string()
        .label('Country')
        .required()
        .oneOf(PAYPAL_CONFIG.withdrawAllowedCountryCodes, countryNotInListErrorMessage);

  return object().shape({
    amount: number().label('Amount').test(testAmount),
    currency: string().label('Currency').oneOf(Object.keys(MIN_WITHDRAW_PAYPAL_AMOUNTS)),
    destinationEmail: string().label('Email').required().email(),
    isEmailConfirmed: mixed().test({
      name: 'isEmailConfirmed',
      message: 'Confirm your email address',
      test: function (value: boolean) {
        return value;
      },
    }),
    country: countryValidation,
  });
};
